class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll("button:not(.localization-selector)").forEach(
      (button) =>
        button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 989px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    document.body.classList.remove(`overflow-hidden-undefined`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }
  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("mobile-menu-nav", HeaderDrawer);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    if (this.classList.contains("slideshow")) {
      this.slider = this;
    } else {
      this.slider = this.querySelector('[id^="Slider-"]');
    }

    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.sliderCounterItems = this.querySelectorAll(".slider-counter--item");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.sliderCounterItems.forEach((counterItem) => {
      counterItem.addEventListener("click", this.onCounterItemClick.bind(this))
    });

    this.dragData = {
      isDragging: false,
      startX: 0,
      currentX: 0,
      sliderScrollPosition: 0,
      sliderWidth: this.slider.offsetWidth
    };

    if(this.dataset.dragDesktop === "true") {
      this.slider.addEventListener("mousedown", this.dragStart.bind(this));
      this.slider.addEventListener("mousemove", this.dragMove.bind(this));
      this.slider.addEventListener("mouseup", this.dragEnd.bind(this));
      this.slider.addEventListener("mouseleave", this.dragEnd.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();

  }

  dragStart(event) {
    this.dragData.sliderWidth = this.slider.offsetWidth;
    this.dragData.isDragging = true;
    this.dragData.startX = event.clientX;
    this.dragData.sliderScrollPosition = this.slider.scrollLeft;
    if(this.sliderItemsToShow.length > 1) {
      this.slider.style.cursor = "grabbing";
    }
  }

  dragMove(event) {
    if (!this.dragData.isDragging) return;

    requestAnimationFrame(() => {
      this.dragData.currentX = event.clientX;
      const dragDistance = this.dragData.startX - this.dragData.currentX;

      if(dragDistance > 200) {
        const slideScrollPosition = this.dragData.sliderScrollPosition + this.dragData.sliderWidth;
        this.slider.scrollTo({
          left: slideScrollPosition,
        });
      } else if(dragDistance < -200) {
        const slideScrollPosition = this.dragData.sliderScrollPosition - this.dragData.sliderWidth;
        this.slider.scrollTo({
          left: slideScrollPosition,
        });
      }
    });
  }

  dragEnd() {
    this.dragData.isDragging = false;
    if(this.sliderItemsToShow.length > 1) {
      this.slider.style.cursor = "grab";
    }
  }

  mouseLeave() {
    this.dragData.isDragging = false;
    if(this.sliderItemsToShow.length > 1) {
      this.slider.style.cursor = "grab";
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if(this.sliderCounterItems.length > 0) {
      this.sliderCounterItems.forEach((counterItem) => {
        counterItem.classList.remove("is-active");
      });
      if (this.sliderCounterItems[this.currentPage - 1]) this.sliderCounterItems[this.currentPage - 1].classList.add("is-active");
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton?.setAttribute("disabled", "disabled");
    } else {
      this.prevButton?.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton?.setAttribute("disabled", "disabled");
    } else {
      this.nextButton?.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  onCounterItemClick(event) {
    event.preventDefault();
    const slideIndex = parseInt(event.currentTarget.dataset.index);
    this.slideScrollPosition = slideIndex * this.sliderItemOffset;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  connectedCallback() {
    // Fix a bug where carousels are sometimes scrolled to the far left on page load
    if (this.dataset.reset_scroll == "true") {
      this.slider.scrollLeft = 0;
    }
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton?.addEventListener(
      "click",
      this.autoPlayToggle.bind(this)
    );
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton?.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton ||
      this.sliderAutoplayButton?.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton =
      event.target === this.sliderAutoplayButton ||
      this.sliderAutoplayButton?.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton?.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton?.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton?.classList.remove(
        "slideshow__autoplay--paused"
      );
      this.sliderAutoplayButton?.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft +
          this.slider.querySelector(".slideshow__slide").clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (
            !this.querySelector("slideshow-component") &&
            this.classList.contains("complementary-products")
          ) {
            this.remove();
          }

          document.dispatchEvent(new CustomEvent("recommendations:loaded"));

          // Number of cards loaded will be cards.length
          const cards = html.querySelectorAll(".grid__item");

          // When the section is re-loaded with products, liquid within cannot reach the outer feature wrap element
          // Need to add fit-exactly classes with js depending on how many products are loaded
          // With the extra class a width for showing an extra quarter or half overrides the exact fit width
          const feature_card_enabled =
            this.dataset.feature_card_enabled === "true";
          if (feature_card_enabled) {
            const columns_desktop = parseInt(this.dataset.columns_desktop);
            const cards_fit_desktop = cards.length <= columns_desktop;
            const feature_wrap_el = document.querySelector(
              `#shopify-section-${this.dataset.section_id} .carousel__feature-wrap`
            );
            if (cards_fit_desktop) {
              feature_wrap_el.classList.add(
                "carousel__feature-wrap--cards-fit-desktop"
              );
            }
          }

          const parent_section = document.getElementById(
            this.dataset.section_id
          );

          // Need to check recently tab before hiding the whole thing
          const hide_recently_tab = this.dataset.hide_recently_tab === "true";
          const product_handle = this.dataset.product_handle;
          const product_handles_list = localStorage.getItem(
            "shopify_recently_viewed"
          );
          const no_recently_viewed =
            !product_handles_list ||
            product_handles_list == "" ||
            product_handles_list == product_handle;

          if (cards) {
            // Loaded
            this.classList.add("product-recommendations--loaded");
          } else if (hide_recently_tab || no_recently_viewed) {
            // No items - recently not showing - hide the carousel
            if (parent_section) {
              parent_section.classList.add("hidden");
            }
          } else {
            // hide just product recommendations
            // hide both tabs
            const recommendations = parent_section.querySelector(
              "product-recommendations"
            );
            const recently = parent_section.querySelector("recently-viewed");
            const recommendations_tab = parent_section.querySelector(
              ".product-recommendations-tab"
            );
            const recently_tab = parent_section.querySelector(
              ".recently-viewed-tab"
            );

            recommendations_tab.classList.add("hidden");
            recently_tab.classList.add("hidden");
            recommendations.classList.remove("active");
            recently.classList.add("active");
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class UpsellProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector(
          "upsell-product-recommendations"
        );

        if (recommendations) {
          if (recommendations.innerHTML.trim().length == 0) {
            fetch(this.dataset.fallbackurl)
              .then((response) => response.text())
              .then((text) => {
                html.innerHTML = text;
                const recommendations = html.querySelector(
                  "upsell-product-recommendations"
                );
                this.innerHTML = recommendations.innerHTML;
                if (
                  !this.querySelector("slideshow-component") &&
                  this.classList.contains("complementary-products")
                ) {
                  this.remove();
                }
                if (html.querySelector(".grid__item")) {
                  this.classList.add("product-recommendations--loaded");
                }
              });
          } else {
            this.innerHTML = recommendations.innerHTML;
            if (
              !this.querySelector("slideshow-component") &&
              this.classList.contains("complementary-products")
            ) {
              this.remove();
            }
            if (html.querySelector(".grid__item")) {
              this.classList.add("product-recommendations--loaded");
            }
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define(
  "upsell-product-recommendations",
  UpsellProductRecommendations
);

class CrossellProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector(
          "crossell-product-recommendations"
        );

        if (recommendations) {
          if (recommendations.innerHTML.trim().length == 0) {
            fetch(this.dataset.fallbackurl)
              .then((response) => response.text())
              .then((text) => {
                html.innerHTML = text;
                const recommendations = html.querySelector(
                  "crossel-product-recommendations"
                );
                this.innerHTML = recommendations.innerHTML;
                if (
                  !this.querySelector("slideshow-component") &&
                  this.classList.contains("complementary-products")
                ) {
                  this.remove();
                }
                if (html.querySelector(".grid__item")) {
                  this.classList.add("product-recommendations--loaded");
                }
              });
          } else {
            this.innerHTML = recommendations.innerHTML;
            if (
              !this.querySelector("slideshow-component") &&
              this.classList.contains("complementary-products")
            ) {
              this.remove();
            }
            if (html.querySelector(".grid__item")) {
              this.classList.add("product-recommendations--loaded");
            }
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define(
  "crossell-product-recommendations",
  CrossellProductRecommendations
);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("shop-look");

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item, .slider__slide")) {
          this.classList.add("shop-look--loaded");
        }

        var shopLookGrid = this.querySelector(".shop-the-look-product-grid");
        var shopLookGridChildren =
          shopLookGrid?.querySelectorAll(".grid__item");

        var shopLookSlider = this.querySelector("shop-look ul.slider");
        var shopLookSliderChildren = shopLookSlider?.querySelectorAll(
          ".grid__item.slider__slide"
        );

        if (shopLookGridChildren?.length > 0) {
          this.closest(".shop-the-look-container")?.classList.remove("hidden");
        } else {
          this.closest(".shop-the-look-container")?.classList.add("hidden");
        }

        if (shopLookSliderChildren?.length > 0 || Shopify.designMode) {
          this.closest(".carousel")?.classList.remove("hidden");
        } else {
          this.closest(".carousel")?.classList.add("hidden");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("shop-look", ShopTheLook);

class RecentlyViewed extends HTMLElement {
  /*
    data-full_width="{{ full_width }}"
    data-max_cards_to_output="{{ max_cards_to_output }}"
    data-sectionid="{{ section.id }}"
    data-columns_desktop="{{ columns_desktop }}"
    data-columns_mobile="{{ columns_mobile }}"
    data-feature_card_enabled="{{ section.settings.feature_card_enabled }}"
    data-recently_is_only_tab="{{ recently_is_only_tab }}"
    data-on_product_page="{{ on_product_page }}"
  */

  constructor() {
    super();
  }

  config = {
    maxToShow: parseInt(this.dataset.max_cards_to_output),
    minToShow: 1,
    howManyToStoreInMemory: parseInt(this.dataset.max_cards_to_output),
    numberProducts: 0,
    onComplete: null,
  };
  productHandleQueue = [];

  cookie = {
    name: "shopify_recently_viewed",
    write: function (recentlyViewed) {
      localStorage.setItem(this.name, recentlyViewed.join(","));
    },
    read: function () {
      let recentlyViewed = [];
      let cookieValue = localStorage.getItem(this.name);
      if (cookieValue) {
        recentlyViewed = cookieValue.split(",");
      }
      return recentlyViewed;
    },
    destroy: function () {
      localStorage.removeItem(this.name);
    },
    remove: function (productHandle) {
      let recentlyViewed = this.read();
      let position = recentlyViewed.indexOf(productHandle);
      if (position !== -1) {
        recentlyViewed.splice(position, 1);
        this.write(recentlyViewed);
      }
    },
  };

  finalize = () => {
    // Add and remove classes that depend on the number of cards

    // Get elements
    const sliderComponent = this.querySelector("slider-component");
    const sliderUL = this.querySelector("slider-component ul.slider");
    const featureWrap = document.querySelector(
      `#shopify-section-${this.dataset.section_id}.carousel__feature-wrap`
    );
    const max_cards_to_output = this.config.maxToShow;

    // Hide buttons if cards fit
    const columns_desktop = parseInt(this.dataset.columns_desktop);
    const columns_mobile = parseInt(this.dataset.columns_mobile);
    let cards_fit_desktop = sliderUL.children.length <= columns_desktop;
    const cards_fit_mobile = sliderUL.children.length <= columns_mobile;
    const feature_card_enabled = this.dataset.feature_card_enabled === "true";
    if (feature_card_enabled) {
      cards_fit_desktop = sliderUL.children.length < columns_desktop;
    } else {
      cards_fit_desktop = sliderUL.children.length <= columns_desktop;
    }

    if (cards_fit_desktop) {
      sliderComponent.classList.add("slider-component--cards-fit-desktop");
    } else {
      sliderComponent.classList.remove("slider-component--cards-fit-desktop");
    }

    if (cards_fit_mobile) {
      sliderComponent.classList.add("slider-component--cards-fit-mobile");
    } else {
      sliderComponent.classList.remove("slider-component--cards-fit-mobile");
    }

    if (featureWrap) {
      if (cards_fit_desktop) {
        featureWrap.classList.add("carousel__feature-wrap--cards-fit-desktop");
      } else {
        featureWrap.classList.remove(
          "carousel__feature-wrap--cards-fit-desktop"
        );
      }
    }

    if (this.dataset.full_width === "true") {
      // set full_width class on parent
      sliderComponent.classList.add("slider-component-full-width");
      sliderComponent.classList.remove("page-width");

      // Add dummy list items for padding

      /*
      const li = document.createElement('li');
      li.setAttribute('aria-hidden', 'true');
      li.setAttribute('class', 'slider__slide first-dummy');
      li.innerHTML = '<!--fake padding-->';

      const li_2 = document.createElement('li');
      li_2.setAttribute('aria-hidden', 'true');
      li_2.setAttribute('class', 'slider__slide last-dummy');
      li_2.innerHTML = '<!--fake padding-->';
      */

      sliderUL.prepend(li);
      sliderUL.appendChild(li_2);
    } else {
      sliderComponent.classList.remove("slider-component-full-width");
      sliderComponent.classList.add("page-width");
    }

    sliderUL.scrollLeft = 0;
    this.classList.add("recently-viewed--loaded");
  };

  getRecentProducts = async () => {
    let searchRecentlyUrl;
    let productHandle;

    if (window.location.pathname.indexOf("/products/") !== -1) {
      productHandle = window.location.pathname.match(
        /\/products\/([a-z0-9\-]+)/
      )[1];
      this.productHandleQueue = this.productHandleQueue.filter(
        (handle) => productHandle !== handle
      );
    }

    if (this.productHandleQueue.length < this.config.minToShow) {
      return;
    }
    this.config.numberProducts = this.productHandleQueue.length;

    this.productHandleQueue.length = this.config.maxToShow;
    searchRecentlyUrl =
      "/search?view=ua_recently-viewed&q=" + this.productHandleQueue.toString();
    await fetch(searchRecentlyUrl)
      .then((response) => response.text())
      .then((html) => {
        document.querySelector(
          `#shopify-section-${this.dataset.sectionid} recently-viewed`
        ).innerHTML = html;
        this.finalize();
      })
      .catch((err) => {
        console.warn("Something went wrong.", err);
      });
  };

  recordRecentlyViewed = (values) => {
    let vals = values || {};
    let recentlyViewed = this.cookie.read();
    let productHandle;
    let position;

    // Update defaults.
    this.config = { ...this.config, ...vals };

    // If we are on a product page.
    if (window.location.pathname.indexOf("/products/") !== -1) {
      productHandle = window.location.pathname.match(
        /\/products\/([a-z0-9\-]+)/
      )[1];

      position = recentlyViewed.indexOf(productHandle);

      if (position === -1) {
        recentlyViewed.unshift(productHandle);

        recentlyViewed = recentlyViewed.splice(
          0,
          this.config.howManyToStoreInMemory
        );
      } else {
        recentlyViewed.splice(position, 1);
        recentlyViewed.unshift(productHandle);
      }

      this.cookie.write(recentlyViewed);
    }
  };

  showRecentlyViewed = (parameters) => {
    // Hide whole carousel if only tab and nothing to show
    const recently_is_only_tab = this.dataset.recently_is_only_tab === "true";
    const sectionid = this.dataset.section_id;
    const carousel = document.querySelector(".carousel.section-" + sectionid);
    const product_handles_list = localStorage.getItem(
      "shopify_recently_viewed"
    );
    if (this.dataset.on_product_page === "true") {
      const product_handle = this.dataset.product_handle;
      const no_recently_viewed =
        !product_handles_list ||
        product_handles_list == "" ||
        product_handles_list == product_handle;
      if (recently_is_only_tab && no_recently_viewed && carousel) {
        carousel.classList.add("hidden");
      } else {
        const no_recently_viewed =
          !product_handles_list || product_handles_list == "";
        if (recently_is_only_tab && no_recently_viewed && carousel) {
          carousel.classList.add("hidden");
        }
      }
    }

    let params = parameters || {};
    // Update defaults.
    this.config = { ...this.config, ...params };

    // Read cookie.
    this.productHandleQueue = this.cookie.read();

    if (
      this.productHandleQueue === null ||
      this.productHandleQueue === undefined
    ) {
      return false;
    }

    this.config.maxToShow = Math.min(
      this.productHandleQueue.length,
      this.config.maxToShow
    );

    if (this.config.maxToShow) {
      this.getRecentProducts().then();
    }
  };

  connectedCallback() {
    this.showRecentlyViewed();
    this.recordRecentlyViewed();
  }
}

customElements.define("recently-viewed", RecentlyViewed);
